import React, { createContext, useState, useContext, useEffect } from 'react';
import { useRange } from '../context/rangeContext';

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: () => void;
  logout: () => void;
  handleAuthCallback: (loginTicket: string) => Promise<void>;
  handleSamlAuthCallback:() => Promise<void>;
  isLoading: boolean;
}


const AuthContext = createContext<AuthContextType | null>(null);
const local = window?.config?.CREDENTIALS?.LOCAL_MODE;
const BE_URL = window?.config?.CREDENTIALS?.REACT_BE_APP_URL;
const isSaml = !!BE_URL && window.location.href.includes(BE_URL);
const baseUrl = local
  ? window?.config?.CREDENTIALS?.BACKEND_READER_URL
  : (isSaml ? BE_URL : window?.config?.CREDENTIALS?.REACT_APP_URL);
const basePathname = isSaml ? "/auth-saml" : "/auth";
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null); 
  const [isLoading, setIsLoading] = useState(true);
  const { clearKpiRanges } = useRange();

  const fetchUser = async () => {
    try {
      const userResponse = await fetch(`${baseUrl}${basePathname}/me`, {
          credentials: 'include'
      });
      setIsAuthenticated(userResponse.ok);

      if (userResponse.ok) {
        const userData = await userResponse.json();
        setUser(userData);
      }
    } catch (error) {
        console.error("Failed to fetch user info", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  const login = () => {
    clearKpiRanges();
    window.location.href = `${baseUrl}${basePathname}/login`;

  };
  

  const logout = async () => {

    try {
      await fetch(`${baseUrl}${basePathname}/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error("Logout failed", error);
    } finally {
      setIsAuthenticated(false);
      setUser(null);
      localStorage.clear();
      window.location.href = '/'; 
    }
  };

    const handleSamlAuthCallback = async () => {
     setIsLoading(true);
      const userResponse = await fetch(`${baseUrl}${basePathname}/me`, {
        credentials: 'include',
      });
      setIsAuthenticated(userResponse.ok);
      if (userResponse.ok) {
        const userData = await userResponse.json();
        setUser(userData);
        setIsLoading(false);
      } else {
        setIsLoading(false);
        throw new Error('Failed to fetch user info');
      }
    }


  const handleAuthCallback = async (loginTicket: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${baseUrl}${basePathname}/exchange`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ loginTicket }),
      });

      if (!response.ok) {
        throw new Error('Failed to exchange login ticket');
      }

      fetchUser();
      sessionStorage.setItem('dismissed_communications',  JSON.stringify([]));


    } catch (error) {
      console.error(error);
      window.location.href = '/';
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    isAuthenticated,
    user,
    login,
    logout,
    handleAuthCallback,
    handleSamlAuthCallback,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export type User = {
  id: string;
  login: string;
  firstName: string;
  lastName: string;
  email: string;
  imageUrl: string | null;
  activated: boolean;
  langKey: string;
  authorities: string[];
  sites: string[];
  ldapToken: string | null;
  roles: string[];
  fr_cockpit_360: string[];
  countryKey: string;
};
